/**
 * jw-product-gallery.js
 * Phase F2.4 — Gallery Module (State Adapter + DOM Render)
 *
 * 职责：Gallery 索引管理 + 主图列表访问 + DOM 渲染
 *
 * 依赖：
 *   JWProductContext  (read-only: images.main)
 *   JWProductState    (read/write: mi, activeSku)
 *
 * Contract: docs/contracts/product_gallery_v1.md
 */
(function () {
  'use strict';

  var _ctx   = null;
  var _state = null;
  var _qsa   = null;  // document.querySelectorAll helper
  var _thumbsBound = false;

  // ── DOM helpers ──
  function $sel(selector) {
    try { return document.querySelector(selector); } catch (e) { return null; }
  }
  function $all(selector) {
    try {
      var nodes = document.querySelectorAll(selector);
      return Array.prototype.slice.call(nodes);
    } catch (e) { return []; }
  }

  /**
   * Initialize gallery adapter.
   *
   * @param {object} context — JWProductContext
   * @param {object} state   — JWProductState (flat mutable S)
   * @returns {object} JWProductGallery API
   */
  function init(context, state) {
    _ctx   = context || {};
    _state = state || {};
    return window.JWProductGallery;
  }

  /**
   * Get current main image index.
   * @returns {number}
   */
  function getIndex() {
    return _state.mi || 0;
  }

  /**
   * Set main image index (writes to State.mi).
   * Does NOT update DOM — caller (F2.4.2) handles rendering.
   *
   * @param {number} i — target index
   */
  function setIndex(i) {
    var total = getTotal();
    if (total === 0) return;
    // Floor + clamp to valid range
    var idx = Math.max(0, Math.min(Math.floor(Number(i)) || 0, total - 1));
    _state.mi = idx;
  }

  /**
   * Get mainImages array from Context.
   * @returns {string[]}
   */
  function getMainImages() {
    if (_ctx.images && Array.isArray(_ctx.images.main)) {
      return _ctx.images.main;
    }
    return [];
  }

  /**
   * Get total number of main images.
   * @returns {number}
   */
  function getTotal() {
    return getMainImages().length;
  }

  /**
   * Get current image URL.
   * @returns {string}
   */
  function getCurrentImage() {
    var imgs = getMainImages();
    var idx  = getIndex();
    return (idx >= 0 && idx < imgs.length) ? imgs[idx] : '';
  }

  /**
   * Go to previous image (wraps around).
   * Also clears activeSku (emancipates gallery from SKU binding).
   */
  function prev() {
    var total = getTotal();
    if (total === 0) return;
    _state.mi = (_state.mi - 1 + total) % total;
    _state.activeSku = null;
  }

  /**
   * Go to next image (wraps around).
   * Also clears activeSku.
   */
  function next() {
    var total = getTotal();
    if (total === 0) return;
    _state.mi = (_state.mi + 1) % total;
    _state.activeSku = null;
  }

  /**
   * Reset gallery to first image, clear SKU binding.
   */
  function reset() {
    _state.mi = 0;
    _state.activeSku = null;
  }

  /**
   * Check if gallery has any images to display.
   * @returns {boolean}
   */
  function hasImages() {
    return getTotal() > 0;
  }

  // ═══════════════ F2.4.2: DOM Render Layer ═══════════════

  /**
   * Update mobile dot indicators.
   * Dots are Liquid-rendered, JS only toggles .on class.
   */
  function _renderMobDots() {
    var dots = $all('#mobDots .mob-dot');
    var idx  = getIndex();
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle('on', i === idx);
    }
  }

  /**
   * Render current gallery state to DOM.
   *
   * Reads: Context.images.main[], State.mi
   * Writes: #mainImg, #mobImg, #imgCnt, #mobCnt, #thList [data-ti], #mobDots .mob-dot
   *
   * This is the single DOM entry point for gallery updates.
   * Replaces the inline setMain() function.
   */
  function render() {
    var imgs  = getMainImages();
    var idx   = getIndex();
    var total = imgs.length;

    if (total === 0) return;

    var src = (idx >= 0 && idx < total) ? imgs[idx] : '';

    // ── PC main image ──
    var mi = $sel('#mainImg');
    if (mi && src) mi.src = src;

    // ── PC counter ──
    var cnt = $sel('#imgCnt');
    if (cnt) cnt.textContent = (idx + 1) + ' / ' + total;

    // ── Mobile main image ──
    var mob = $sel('#mobImg');
    if (mob && src) mob.src = src;

    // ── Mobile counter ──
    var mc = $sel('#mobCnt');
    if (mc) mc.textContent = (idx + 1) + ' / ' + total;

    // ── Mobile dots ──
    _renderMobDots();

    // ── Thumbnail highlight ──
    var thumbs = $all('#thList [data-ti]');
    for (var t = 0; t < thumbs.length; t++) {
      var el = thumbs[t];
      var ti = +(el.getAttribute('data-ti') || 0);
      var isActive = ti === idx;
      el.classList.toggle('border-gray-900', isActive);
      el.classList.toggle('border-transparent', !isActive);
      el.classList.toggle('ring-2', isActive);
      el.classList.toggle('ring-gray-900', isActive);
    }

    // ── Scroll active thumbnail into view ──
    var ac = $sel('#thList [data-ti="' + idx + '"]');
    if (ac) ac.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  /**
   * Bind thumbnail click events (one-time only).
   *
   * Replaces inline bindThumbEvents().
   * On click: setIndex → clearSku → render → callback (for variant/pricing domains).
   *
   * @param {function} afterRender — optional callback for non-gallery side effects
   */
  function bindThumbEvents(afterRender) {
    if (_thumbsBound) return;
    _thumbsBound = true;

    var cb = (typeof afterRender === 'function') ? afterRender : function () {};

    // PC thumbnails: #thList [data-ti]
    var pcThumbs = $all('#thList [data-ti]');
    for (var i = 0; i < pcThumbs.length; i++) {
      (function (el) {
        el.onclick = function () {
          var idx = +(el.getAttribute('data-ti') || 0);
          _state.mi = idx;
          _state.activeSku = null;
          render();
          cb();
        };
      })(pcThumbs[i]);
    }

    // Mobile dots: #mobDots .mob-dot  (Liquid-rendered, uses data-dot)
    var mobDots = $all('#mobDots .mob-dot');
    for (var d = 0; d < mobDots.length; d++) {
      (function (el) {
        el.onclick = function () {
          var idx = +(el.getAttribute('data-dot') || 0);
          _state.mi = idx;
          _state.activeSku = null;
          render();
          cb();
        };
      })(mobDots[d]);
    }
  }

  var _navBound = false;

  /**
   * Bind navigation events: prev/next buttons + touch swipe.
   *
   * Migrated from inline #mPrev/#mNext/#mobPrev/#mobNext + touch handler.
   * All direction changes go through JWGal.next()/prev() — zero direct S.mi mutation.
   *
   * @param {function} afterNav — optional callback for non-gallery side effects
   */
  function bindNavEvents(afterNav) {
    if (_navBound) return;
    _navBound = true;

    var cb = (typeof afterNav === 'function') ? afterNav : function () {};

    // ── PC prev/next ──
    var mPrev = $sel('#mPrev');
    if (mPrev) mPrev.onclick = function () { prev(); render(); cb(); };

    var mNext = $sel('#mNext');
    if (mNext) mNext.onclick = function () { next(); render(); cb(); };

    // ── Mobile prev/next ──
    var mobPrev = $sel('#mobPrev');
    if (mobPrev) mobPrev.onclick = function () { prev(); render(); cb(); };

    var mobNext = $sel('#mobNext');
    if (mobNext) mobNext.onclick = function () { next(); render(); cb(); };

    // ── Touch swipe on mobile ──
    var tSX = 0, tSY = 0;
    var swiper = $sel('#mobSwiper');
    if (swiper) {
      swiper.addEventListener('touchstart', function (e) {
        tSX = e.touches[0].clientX;
        tSY = e.touches[0].clientY;
      }, { passive: true });

      swiper.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - tSX;
        var dy = e.changedTouches[0].clientY - tSY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
          if (dx < 0) next(); else prev();
          render();
          cb();
        }
      });
    }
  }

  // ═══════════════ F2.4.2 Step D: Fullscreen Viewer ═══════════════

  var _viewerBound = false;

  /**
   * Render viewer thumbnail strip (PC + Mobile).
   * Creates DOM elements for each image in S.vList.
   */
  function _renderViewerThumbs() {
    var vList  = _state.vList || [];
    var vIdx   = _state.vIdx || 0;
    var vpc    = $sel('#vThumbsPC');
    var vmob   = $sel('#vThumbsMob');
    var vImgEl = $sel('#vImg');

    if (vpc) vpc.innerHTML = '';
    if (vmob) vmob.innerHTML = '';

    for (var i = 0; i < vList.length; i++) {
      (function (idx, src) {
        var isActive = idx === vIdx;

        // PC thumb
        if (vpc) {
          var d = document.createElement('div');
          d.className = 'w-[80px] h-[80px] shrink-0 rounded-md overflow-hidden border-2 cursor-pointer transition '
            + (isActive ? 'border-white' : 'border-transparent hover:border-white/50');
          d.innerHTML = '<img src="' + src + '" class="w-full h-full object-cover">';
          d.onclick = function () {
            viewerSetIndex(idx);
          };
          vpc.appendChild(d);
        }

        // Mobile thumb
        if (vmob) {
          var dm = document.createElement('div');
          dm.className = 'w-[60px] h-[60px] shrink-0 rounded-md overflow-hidden border-2 cursor-pointer transition '
            + (isActive ? 'border-white' : 'border-transparent hover:border-white/50');
          dm.innerHTML = '<img src="' + src + '" class="w-full h-full object-cover">';
          dm.onclick = function () {
            viewerSetIndex(idx);
          };
          vmob.appendChild(dm);
        }
      })(i, vList[i]);
    }
  }

  /**
   * Open fullscreen viewer.
   *
   * @param {string[]} list  — image URLs
   * @param {number}   index — starting index
   */
  function openViewer(list, index) {
    if (!list || !list.length) return;

    _state.vList  = list;
    _state.vIdx   = Math.max(0, Math.min(index || 0, list.length - 1));
    _state.vScale = 1;

    var V       = $sel('#viewer');
    var Vimg    = $sel('#vImg');
    var Vpage   = $sel('#vPage');

    if (Vimg) {
      Vimg.src = list[_state.vIdx];
      Vimg.style.transform = 'translate(0,0) scale(1)';
    }
    if (Vpage) Vpage.textContent = (_state.vIdx + 1) + ' / ' + list.length;
    if (V) { V.classList.add('on'); }
    if (document.body) document.body.style.overflow = 'hidden';

    _renderViewerThumbs();
  }

  /**
   * Close fullscreen viewer.
   */
  function closeViewer() {
    var V = $sel('#viewer');
    if (V) V.classList.remove('on');
    if (document.body) document.body.style.overflow = '';
  }

  /**
   * Set viewer index and re-render.
   */
  function viewerSetIndex(i) {
    var vList  = _state.vList || [];
    var vImgEl = $sel('#vImg');
    var vPage  = $sel('#vPage');

    _state.vIdx   = Math.max(0, Math.min(i || 0, vList.length - 1));
    _state.vScale = 1;

    if (vImgEl) {
      vImgEl.src = vList[_state.vIdx];
      vImgEl.style.transform = 'translate(0,0) scale(1)';
    }
    if (vPage) vPage.textContent = (_state.vIdx + 1) + ' / ' + vList.length;

    _renderViewerThumbs();
  }

  /**
   * Viewer previous image (wraps).
   */
  function viewerPrev() {
    var vList = _state.vList || [];
    if (vList.length === 0) return;
    viewerSetIndex((_state.vIdx - 1 + vList.length) % vList.length);
  }

  /**
   * Viewer next image (wraps).
   */
  function viewerNext() {
    var vList = _state.vList || [];
    if (vList.length === 0) return;
    viewerSetIndex((_state.vIdx + 1) % vList.length);
  }

  /**
   * Check if viewer is currently open.
   */
  function isViewerOpen() {
    var V = $sel('#viewer');
    return !!(V && V.classList.contains('on'));
  }

  /**
   * Bind viewer controls (one-time).
   */
  function bindViewerEvents() {
    if (_viewerBound) return;
    _viewerBound = true;

    // Close button
    var vClose = $sel('#vClose');
    if (vClose) vClose.onclick = closeViewer;

    // Backdrop click
    var V = $sel('#viewer');
    if (V) V.onclick = function (e) { if (e.target === V) closeViewer(); };

    // Prev/Next buttons
    var vPrev = $sel('#vPrev');
    if (vPrev) vPrev.onclick = viewerPrev;

    var vNext = $sel('#vNext');
    if (vNext) vNext.onclick = viewerNext;

    // Keyboard navigation
    document.addEventListener('keydown', function (e) {
      if (!isViewerOpen()) return;
      if (e.key === 'Escape') closeViewer();
      if (e.key === 'ArrowLeft') viewerPrev();
      if (e.key === 'ArrowRight') viewerNext();
    });
  }

  // ── Expose ──
  window.JWProductGallery = {
    init:            init,
    getIndex:        getIndex,
    setIndex:        setIndex,
    getMainImages:   getMainImages,
    getTotal:        getTotal,
    getCurrentImage: getCurrentImage,
    prev:            prev,
    next:            next,
    reset:           reset,
    hasImages:       hasImages,
    render:          render,
    bindThumbEvents: bindThumbEvents,
    bindNavEvents:   bindNavEvents,
    openViewer:      openViewer,
    closeViewer:     closeViewer,
    viewerSetIndex:  viewerSetIndex,
    viewerPrev:      viewerPrev,
    viewerNext:      viewerNext,
    isViewerOpen:    isViewerOpen,
    bindViewerEvents: bindViewerEvents
  };

})();
